// src/components/DriverForm.js - UPDATED with document uploads
import React, { useState, useContext, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ScrollView,
} from 'react-native';
import { useTranslation } from '../i18n';
import { AppContext } from "../../App";
import api from '../services/api';
import LicenseCategoryDropdown from './LicenseCategoryDropdown';
import DriverDocumentUpload from './DriverDocumentUpload';
import { uploadDriverDocument } from '../api/driverDocumentApi';
import { validatePhoneNumber, validateEmail, cleanPhoneNumber } from '../utils/validation';

const COLORS = {
  primary: '#14B8A6',
  background: '#F7F9FA',
  card: '#FFFFFF',
  textDark: '#1C1C1E',
  textMuted: '#6B7280',
  error: '#DC2626',
  success: '#16A34A',
  warning: '#F59E0B',
  border: '#E5E7EB',
};

const EMPTY_DRIVER_DOCUMENT = {
  front: null,
  back: null,
  pdf: null,
  skipped: false,
};

function emptyDriverDocument() {
  return { ...EMPTY_DRIVER_DOCUMENT };
}

function skippedDriverDocument() {
  return {
    front: null,
    back: null,
    pdf: null,
    skipped: true,
  };
}

function normalizeDriverDocumentValue(value) {
  if (!value) return emptyDriverDocument();

  if (
    Object.prototype.hasOwnProperty.call(value, 'front') ||
    Object.prototype.hasOwnProperty.call(value, 'back') ||
    Object.prototype.hasOwnProperty.call(value, 'pdf') ||
    Object.prototype.hasOwnProperty.call(value, 'skipped')
  ) {
    return {
      front: value.front || null,
      back: value.back || null,
      pdf: value.pdf || null,
      skipped: Boolean(value.skipped),
    };
  }

  // Backward compatibility with the old single-file state.
  if (value.uri || value.name || value.mimeType || value.type) {
    return {
      front: value,
      back: null,
      pdf: null,
      skipped: false,
    };
  }

  return emptyDriverDocument();
}

function hasCompleteTwoSidedDocument(value, options = {}) {
  const { photoOnly = false } = options;
  const v = normalizeDriverDocumentValue(value);

  if (v.skipped) return true;

  if (photoOnly) {
    return Boolean(v.front);
  }

  if (v.pdf) return true;

  return Boolean(v.front && v.back);
}

function pushDriverDocumentUploads(uploadPromises, {
  verificationId,
  personId,
  docType,
  value,
  photoOnly = false,
}) {
  const v = normalizeDriverDocumentValue(value);

  if (!verificationId || !personId || v.skipped) return;

  if (v.pdf && !photoOnly) {
    uploadPromises.push(
      uploadDriverDocument(verificationId, personId, docType, v.pdf, 'pdf')
        .catch((err) =>
          console.error(`Failed to upload ${docType} pdf:`, err)
        )
    );

    return;
  }

  if (v.front) {
    uploadPromises.push(
      uploadDriverDocument(verificationId, personId, docType, v.front, 'front')
        .catch((err) =>
          console.error(`Failed to upload ${docType} front:`, err)
        )
    );
  }

  if (!photoOnly && v.back) {
    uploadPromises.push(
      uploadDriverDocument(verificationId, personId, docType, v.back, 'back')
        .catch((err) =>
          console.error(`Failed to upload ${docType} back:`, err)
        )
    );
  }
}

const DriverForm = ({
  vehicleId,
  verificationId,
  onDriverSaved,
  onAllDataSaved, // New callback for when both driver and docs are saved
  initialDriverName = '',
}) => {
  const { t } = useTranslation();
  const { verification, setVerification } = useContext(AppContext);
  
  // Check if driver already has a personId in context
  const existingDriverPersonId = verification?.driverPersonId;
  const [isNoLongerDriver, setIsNoLongerDriver] = useState(false);
  
  const [driver, setDriver] = useState({
    name: initialDriverName,
    phone: '',
    email: '',
    birthDate: '',
    gender: '',
    cinNumber: '',
    taxpayerNumber: '',
    licenseNumber: '',
    licenseCategory: '',
    driverSyndicate: '',
    // Capacity fields
    hasCapacityDoc: true,
    capacityNumber: '',
    // Secure badge fields
    hasSecureBadgeDoc: true,
    secureBadge: null,
    // Document files
    driverPhotoFile: emptyDriverDocument(),
    driverLicenseFile: emptyDriverDocument(),
    driverIdCardFile: emptyDriverDocument(),
    driverCapacityFile: emptyDriverDocument(),
    driverResidenceCardFile: emptyDriverDocument(),
    driverProfessionalBadgeFile: emptyDriverDocument(),

    // Document status flags
    hasDriverPhoto: false,
    hasDriverLicense: false,
    hasDriverIdCard: false,
    hasDriverCapacity: false,
    hasDriverResidenceCard: false,
    hasDriverProfessionalBadge: false,
  });
  
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaved, setIsSaved] = useState(!!existingDriverPersonId);
  const [savedPersonId, setSavedPersonId] = useState(existingDriverPersonId || null);
  const [uploadProgress, setUploadProgress] = useState(false);

  useEffect(() => {
    if (existingDriverPersonId) {
      setIsSaved(true);
      setSavedPersonId(existingDriverPersonId);
    }
  }, [existingDriverPersonId]);

  const validate = () => {
    const newErrors = {};
    
    if (!driver.name.trim()) {
      newErrors.name = t('common.required');
    }
    
    if (!driver.phone.trim()) {
      newErrors.phone = t('common.required');
    } else {
      const phoneValidation = validatePhoneNumber(driver.phone, { required: true });
      if (!phoneValidation.isValid) {
        newErrors.phone = phoneValidation.error;
      }
    }
    
    if (driver.email && driver.email.trim()) {
      const emailValidation = validateEmail(driver.email, false);
      if (!emailValidation.isValid) {
        newErrors.email = emailValidation.error;
      }
    }
    
    if (driver.birthDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(driver.birthDate)) {
      newErrors.birthDate = t('common.invalidDate');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneChange = (text) => {
    const cleaned = text.replace(/\D/g, '');
    setDriver({ ...driver, phone: cleaned });
    
    if (errors.phone) {
      setErrors({ ...errors, phone: null });
    }
  };

  const handleDocumentFile = (docType, value) => {
    const normalized = normalizeDriverDocumentValue(value);

    switch (docType) {
      case 'DRIVER_PHOTO':
        setDriver((prev) => ({
          ...prev,
          driverPhotoFile: normalized,
          hasDriverPhoto: hasCompleteTwoSidedDocument(normalized, { photoOnly: true }),
        }));
        break;

      case 'DRIVER_LICENSE':
        setDriver((prev) => ({
          ...prev,
          driverLicenseFile: normalized,
          hasDriverLicense: hasCompleteTwoSidedDocument(normalized),
        }));
        break;

      case 'DRIVER_ID_CARD':
        setDriver((prev) => ({
          ...prev,
          driverIdCardFile: normalized,
          hasDriverIdCard: hasCompleteTwoSidedDocument(normalized),
        }));
        break;

      case 'DRIVER_CAPACITY':
        setDriver((prev) => ({
          ...prev,
          driverCapacityFile: normalized,
          hasDriverCapacity: hasCompleteTwoSidedDocument(normalized),
        }));
        break;

      case 'DRIVER_RESIDENCE_CARD':
        setDriver((prev) => ({
          ...prev,
          driverResidenceCardFile: normalized,
          hasDriverResidenceCard: hasCompleteTwoSidedDocument(normalized),
        }));
        break;

      case 'DRIVER_PROFESSIONAL_BADGE':
        setDriver((prev) => ({
          ...prev,
          driverProfessionalBadgeFile: normalized,
          hasDriverProfessionalBadge: hasCompleteTwoSidedDocument(normalized),
        }));
        break;

      default:
        break;
    }
  };

  const handleDocumentSkip = (docType) => {
    handleDocumentFile(docType, skippedDriverDocument());
  };

  const uploadDriverDocuments = async (personId) => {
    const uploadPromises = [];

    pushDriverDocumentUploads(uploadPromises, {
      verificationId,
      personId,
      docType: 'DRIVER_PHOTO',
      value: driver.driverPhotoFile,
      photoOnly: true,
    });

    pushDriverDocumentUploads(uploadPromises, {
      verificationId,
      personId,
      docType: 'DRIVER_LICENSE',
      value: driver.driverLicenseFile,
    });

    pushDriverDocumentUploads(uploadPromises, {
      verificationId,
      personId,
      docType: 'DRIVER_ID_CARD',
      value: driver.driverIdCardFile,
    });

    pushDriverDocumentUploads(uploadPromises, {
      verificationId,
      personId,
      docType: 'DRIVER_CAPACITY',
      value: driver.driverCapacityFile,
    });

    pushDriverDocumentUploads(uploadPromises, {
      verificationId,
      personId,
      docType: 'DRIVER_RESIDENCE_CARD',
      value: driver.driverResidenceCardFile,
    });

    pushDriverDocumentUploads(uploadPromises, {
      verificationId,
      personId,
      docType: 'DRIVER_PROFESSIONAL_BADGE',
      value: driver.driverProfessionalBadgeFile,
    });

    if (uploadPromises.length > 0) {
      setUploadProgress(true);
      await Promise.allSettled(uploadPromises);
      setUploadProgress(false);
    }
  };

  const handleSave = async () => {
    if (isSaved) {
      Alert.alert(
        t('driverManager.alreadySaved'),
        t('driverManager.alreadySavedMessage')
      );
      return;
    }

    if (!validate()) {
      Alert.alert(t('common.error'), t('driverManager.correctFields'));
      return;
    }

    if (!verificationId) {
      Alert.alert(t('common.error'), 'Verification ID is missing');
      return;
    }

    setSaving(true);
    try {
      const cleanedPhone = cleanPhoneNumber(driver.phone);
      
      // STEP 1: Save driver information
      const response = await api.put(`/verification/${verificationId}/extra`, {
        vehicle: {},
        owner: {},
        drivers: [{
          name: driver.name.trim(),
          phone: cleanedPhone,
          email: driver.email?.trim() || null,
          birthDate: driver.birthDate || null,
          gender: driver.gender || null,
          cinNumber: driver.cinNumber?.trim() || null,
          taxpayerNumber: driver.taxpayerNumber?.trim() || null,
          licenseNumber: driver.licenseNumber?.trim() || null,
          licenseCategory: driver.licenseCategory || null,
          driverSyndicate: driver.driverSyndicate?.trim() || null,
          capacityNumber: driver.capacityNumber || null,
          hasCapacityDoc: driver.hasCapacityDoc,
          hasSecureBadgeDoc: driver.hasSecureBadgeDoc,
          secureBadge: driver.secureBadge,
          personId: null,
          fetchedFromDB: false
        }],
        survey: {}
      });

      const newlyCreatedPersonIds = response.data?.newlyCreatedPersonIds || [];
      
      if (newlyCreatedPersonIds.length > 0) {
        const personId = newlyCreatedPersonIds[0];
        
        // STEP 2: Upload documents if any
        await uploadDriverDocuments(personId);
        
        // STEP 3: Update context
        // setVerification(prev => ({
        //   ...prev,
        //   driverPersonId: personId,
        //   driverAddedInWorkflow: true
        // }));
              // STEP 3: Update context with ALL driver data
      setVerification(prev => ({
        ...prev,
        driverPersonId: personId,
        driverAddedInWorkflow: true,
        driver: {
          ...prev.driver,
          personId,
          name: driver.name.trim(),
          phone: cleanedPhone,
          email: driver.email?.trim(),
          birthDate: driver.birthDate,
          gender: driver.gender,
          cinNumber: driver.cinNumber?.trim(),
          taxpayerNumber: driver.taxpayerNumber?.trim(),
          licenseNumber: driver.licenseNumber?.trim(),
          licenseCategory: driver.licenseCategory,
          driverSyndicate: driver.driverSyndicate?.trim(),
          capacityNumber: driver.capacityNumber,
          hasCapacityDoc: driver.hasCapacityDoc,
          hasSecureBadgeDoc: driver.hasSecureBadgeDoc,
          secureBadge: driver.secureBadge,
          hasDriverPhoto: !!driver.driverPhotoFile,
          hasDriverLicense: !!driver.driverLicenseFile,
          hasDriverIdCard: !!driver.driverIdCardFile,
          hasDriverCapacity: !!driver.driverCapacityFile,
        },
        driverDocuments: {
          ...prev.driverDocuments,
          [personId]: {
            photo: driver.driverPhotoFile,
            license: driver.driverLicenseFile,
            idCard: driver.driverIdCardFile,
            capacity: driver.driverCapacityFile,
          }
        }
      }));
        
        setIsSaved(true);
        setSavedPersonId(personId);
        
        if (onDriverSaved) {
          onDriverSaved(personId);
        }
        
        if (onAllDataSaved) {
          onAllDataSaved(personId);
        }
        
        Alert.alert(
          t('common.success'),
          t('driverManager.driverAndDocsSaved', 'Chauffeur et documents enregistrés avec succès')
        );
      }
    } catch (error) {
      console.error('Error saving driver: - DriverForm.js:320', error);
      Alert.alert(
        t('common.error'), 
        error.response?.data?.error || t('driverManager.saveError')
      );
    } finally {
      setSaving(false);
    }
  };

  const renderSavedBadge = () => {
    if (isSaved) {
      return (
        <View style={styles.savedBadge}>
          <Text style={styles.savedBadgeText}>
            ✅ {t('driverManager.saved')} (ID: {savedPersonId})
          </Text>
        </View>
      );
    }
    return null;
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.card}>
        <Text style={styles.title}>{t('driverForm.addYourself')}</Text>
        <Text style={styles.subtitle}>{t('driverForm.subtitle')}</Text>

        {renderSavedBadge()}

        {/* Basic Info Section */}
        <Text style={styles.sectionTitle}>{t('driverManager.sections.basicInfo')}</Text>

        <Text style={styles.label}>{t('driverManager.fields.fullName')} *</Text>
        <TextInput
          style={[styles.input, errors.name && styles.inputError, isSaved && styles.inputDisabled]}
          value={driver.name}
          onChangeText={(text) => setDriver({ ...driver, name: text })}
          placeholder={t('driverManager.fields.fullName')}
          editable={!saving && !isSaved}
        />
        {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}

        <Text style={styles.label}>{t('driverManager.fields.phone')} *</Text>
        <TextInput
          style={[styles.input, errors.phone && styles.inputError, isSaved && styles.inputDisabled]}
          value={driver.phone}
          onChangeText={handlePhoneChange}
          placeholder="677123456"
          keyboardType="phone-pad"
          maxLength={9}
          editable={!saving && !isSaved}
        />
        {errors.phone && <Text style={styles.errorText}>{errors.phone}</Text>}

        <Text style={styles.label}>{t('driverManager.fields.email')}</Text>
        <TextInput
          style={[styles.input, errors.email && styles.inputError, isSaved && styles.inputDisabled]}
          value={driver.email}
          onChangeText={(text) => setDriver({ ...driver, email: text })}
          placeholder="exemple@email.com"
          keyboardType="email-address"
          autoCapitalize="none"
          editable={!saving && !isSaved}
        />
        {errors.email && <Text style={styles.errorText}>{errors.email}</Text>}

        <Text style={styles.label}>{t('driverManager.fields.birthDate')}</Text>
        <TextInput
          style={[styles.input, errors.birthDate && styles.inputError, isSaved && styles.inputDisabled]}
          value={driver.birthDate}
          onChangeText={(text) => setDriver({ ...driver, birthDate: text })}
          placeholder="DD/MM/YYYY"
          editable={!saving && !isSaved}
        />
        {errors.birthDate && <Text style={styles.errorText}>{errors.birthDate}</Text>}

        <Text style={styles.label}>{t('driverManager.fields.gender')}</Text>
        <View style={styles.row}>
          {[t('driverManager.genders.male'), t('driverManager.genders.female')].map((gender) => (
            <TouchableOpacity
              key={gender}
              style={[
                styles.genderButton,
                driver.gender === gender && styles.genderButtonActive,
                isSaved && styles.buttonDisabled,
              ]}
              onPress={() => setDriver({ ...driver, gender })}
              disabled={saving || isSaved}
            >
              <Text
                style={[
                  styles.genderText,
                  driver.gender === gender && styles.genderTextActive,
                  isSaved && styles.textDisabled,
                ]}
              >
                {gender}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <Text style={styles.label}>{t('driverManager.fields.cinNumber')}</Text>
        <TextInput
          style={[styles.input, isSaved && styles.inputDisabled]}
          value={driver.cinNumber}
          onChangeText={(text) => setDriver({ ...driver, cinNumber: text })}
          placeholder={t('driverManager.fields.cinNumber')}
          editable={!saving && !isSaved}
        />

        <Text style={styles.label}>{t('driverManager.fields.taxpayerNumber')}</Text>
        <TextInput
          style={[styles.input, isSaved && styles.inputDisabled]}
          value={driver.taxpayerNumber}
          onChangeText={(text) => setDriver({ ...driver, taxpayerNumber: text })}
          placeholder={t('driverManager.fields.taxpayerNumber')}
          editable={!saving && !isSaved}
        />

        {/* License Section */}
        <Text style={[styles.sectionTitle, styles.sectionSpacer]}>
          {t('driverManager.sections.licenseInfo')}
        </Text>

        <Text style={styles.label}>{t('driverManager.fields.licenseNumber')}</Text>
        <TextInput
          style={[styles.input, isSaved && styles.inputDisabled]}
          value={driver.licenseNumber}
          onChangeText={(text) => setDriver({ ...driver, licenseNumber: text })}
          placeholder={t('driverManager.fields.licenseNumber')}
          editable={!saving && !isSaved}
        />

        <LicenseCategoryDropdown
          value={driver.licenseCategory}
          onSelect={(category) => setDriver({ ...driver, licenseCategory: category })}
          editable={!saving && !isSaved}
        />

        {/* Capacity Section */}
        <Text style={[styles.sectionTitle, styles.sectionSpacer]}>
          {t('driverManager.sections.capacityInfo')}
        </Text>

        <TouchableOpacity
          style={styles.checkRow}
          onPress={() => {
            const newValue = !driver.hasCapacityDoc;
            setDriver({ ...driver, hasCapacityDoc: newValue });
            if (!newValue) {
              setDriver(prev => ({ ...prev, capacityNumber: '' }));
            }
          }}
          disabled={saving || isSaved}
        >
          <View style={[styles.checkBox, !driver.hasCapacityDoc && styles.checkBoxOn]}>
            {!driver.hasCapacityDoc && <Text style={styles.checkMark}>✓</Text>}
          </View>
          <Text style={styles.checkLabel}>
            {t('driverManager.dontHaveCapacity')}
          </Text>
        </TouchableOpacity>

        {driver.hasCapacityDoc && (
          <>
            <Text style={styles.label}>{t('driverManager.fields.capacityNumber')}</Text>
            <TextInput
              style={[styles.input, isSaved && styles.inputDisabled]}
              value={driver.capacityNumber}
              onChangeText={(text) => setDriver({ ...driver, capacityNumber: text })}
              placeholder={t('driverManager.fields.capacityNumberPlaceholder')}
              editable={!saving && !isSaved}
            />
          </>
        )}

        {/* Secure Badge Section */}
        <Text style={[styles.sectionTitle, styles.sectionSpacer]}>
          {t('driverManager.sections.secureBadge')}
        </Text>

        <TouchableOpacity
          style={styles.checkRow}
          onPress={() => {
            const newValue = !driver.hasSecureBadgeDoc;
            setDriver({ ...driver, hasSecureBadgeDoc: newValue });
            if (!newValue) {
              setDriver(prev => ({ ...prev, secureBadge: null }));
            }
          }}
          disabled={saving || isSaved}
        >
          <View style={[styles.checkBox, !driver.hasSecureBadgeDoc && styles.checkBoxOn]}>
            {!driver.hasSecureBadgeDoc && <Text style={styles.checkMark}>✓</Text>}
          </View>
          <Text style={styles.checkLabel}>
            {t('driverManager.dontHaveSecureBadge')}
          </Text>
        </TouchableOpacity>

        {driver.hasSecureBadgeDoc && (
          <>
            <Text style={styles.label}>{t('driverManager.fields.secureBadge')}</Text>
            <View style={styles.row}>
              <TouchableOpacity
                style={[
                  styles.pillButton,
                  driver.secureBadge === true && styles.pillButtonActive,
                ]}
                onPress={() => setDriver({ ...driver, secureBadge: true })}
                disabled={saving || isSaved}
              >
                <Text style={[
                  styles.pillText,
                  driver.secureBadge === true && styles.pillTextActive,
                ]}>
                  {t('common.yes')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.pillButton,
                  driver.secureBadge === false && styles.pillButtonActive,
                ]}
                onPress={() => setDriver({ ...driver, secureBadge: false })}
                disabled={saving || isSaved}
              >
                <Text style={[
                  styles.pillText,
                  driver.secureBadge === false && styles.pillTextActive,
                ]}>
                  {t('common.no')}
                </Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        <Text style={styles.label}>{t("driverManager.fields.driverSyndicate")}</Text>
        <TextInput
          style={[styles.input, isSaved && styles.inputDisabled]}
          value={driver.driverSyndicate}
          onChangeText={(text) => setDriver({ ...driver, driverSyndicate: text })}
          placeholder={t("driverManager.fields.driverSyndicate")}
          editable={!saving && !isSaved}
        />

        {/* Documents Section - Always visible, collects files before saving */}
        <Text style={[styles.sectionTitle, styles.sectionSpacer]}>
          {t('driverManager.sections.documents')}
          {!isSaved && <Text style={styles.infoNote}></Text>}
        </Text>

          <DriverDocumentUpload
            docType="DRIVER_PHOTO"
            label={t('driverManager.documents.photo')}
            value={driver.driverPhotoFile}
            onFileSelected={(value) => handleDocumentFile('DRIVER_PHOTO', value)}
            onSkip={() => handleDocumentSkip('DRIVER_PHOTO')}
            disabled={isSaved}
          />

          <DriverDocumentUpload
            docType="DRIVER_LICENSE"
            label={t('driverManager.documents.license')}
            value={driver.driverLicenseFile}
            onFileSelected={(value) => handleDocumentFile('DRIVER_LICENSE', value)}
            onSkip={() => handleDocumentSkip('DRIVER_LICENSE')}
            disabled={isSaved}
          />

          <DriverDocumentUpload
            docType="DRIVER_ID_CARD"
            label={t('driverManager.documents.idCard')}
            value={driver.driverIdCardFile}
            onFileSelected={(value) => handleDocumentFile('DRIVER_ID_CARD', value)}
            onSkip={() => handleDocumentSkip('DRIVER_ID_CARD')}
            disabled={isSaved}
          />

          <DriverDocumentUpload
            docType="DRIVER_CAPACITY"
            label={t('driverManager.documents.capacity')}
            value={driver.driverCapacityFile}
            onFileSelected={(value) => handleDocumentFile('DRIVER_CAPACITY', value)}
            onSkip={() => handleDocumentSkip('DRIVER_CAPACITY')}
            disabled={isSaved}
          />

          <DriverDocumentUpload
            docType="DRIVER_RESIDENCE_CARD"
            label="Carte de résidence"
            value={driver.driverResidenceCardFile}
            onFileSelected={(value) => handleDocumentFile('DRIVER_RESIDENCE_CARD', value)}
            onSkip={() => handleDocumentSkip('DRIVER_RESIDENCE_CARD')}
            disabled={isSaved}
          />

          <DriverDocumentUpload
            docType="DRIVER_PROFESSIONAL_BADGE"
            label="Badge professionnel d'identification"
            value={driver.driverProfessionalBadgeFile}
            onFileSelected={(value) => handleDocumentFile('DRIVER_PROFESSIONAL_BADGE', value)}
            onSkip={() => handleDocumentSkip('DRIVER_PROFESSIONAL_BADGE')}
            disabled={isSaved}
          />
          <TouchableOpacity
            style={styles.checkRow}
            onPress={() => setIsNoLongerDriver(!isNoLongerDriver)}
            // disabled={saving}
          >
            <View style={[styles.checkBox, isNoLongerDriver && styles.checkBoxOn]}>
              {isNoLongerDriver && <Text style={styles.checkMark}>✓</Text>}
            </View>
            <Text style={[
              styles.checkLabel,
              isNoLongerDriver && styles.checkLabelActive
            ]}>
              {t('driverManager.noLongerDriver')}
            </Text>
          </TouchableOpacity>
        {/* Save Button */}
        <TouchableOpacity
          style={[
            styles.saveButton, 
            (saving || isSaved || uploadProgress) && styles.saveButtonDisabled,
            isSaved && styles.saveButtonSuccess
          ]}
          onPress={handleSave}
          disabled={saving || isSaved || uploadProgress}
        >
          {saving ? (
            <Text style={styles.saveButtonText}>{t('common.saving')}</Text>
          ) : uploadProgress ? (
            <Text style={styles.saveButtonText}>{t('common.uploading')}</Text>
          ) : isSaved ? (
            <Text style={styles.saveButtonText}>
              ✅ {t('driverManager.saved')}
            </Text>
          ) : (
            <Text style={styles.saveButtonText}>{t('common.save')}</Text>
          )}
        </TouchableOpacity>

        {isSaved && (
          <View style={styles.infoBox}>
            <Text style={styles.infoText}>
              ℹ️ {t('driverManager.alreadySavedInfo')}
            </Text>
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.background,
  },
  card: {
    backgroundColor: COLORS.card,
    borderRadius: 16,
    padding: 16,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: COLORS.textDark,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: COLORS.textMuted,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: COLORS.primary,
    marginBottom: 12,
    marginTop: 8,
  },
  sectionSpacer: {
    marginTop: 16,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    paddingTop: 12,
  },
  infoNote: {
    fontSize: 12,
    color: COLORS.textMuted,
    fontStyle: 'italic',
    fontWeight: '400',
  },
  savedBadge: {
    backgroundColor: COLORS.success + '20',
    padding: 10,
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.success,
  },
  savedBadgeText: {
    color: COLORS.success,
    fontWeight: '600',
    textAlign: 'center',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.textMuted,
    marginTop: 8,
    marginBottom: 4,
  },
  input: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    backgroundColor: COLORS.background,
  },
  inputError: {
    borderColor: COLORS.error,
    backgroundColor: '#FEF2F2',
  },
  inputDisabled: {
    backgroundColor: '#F3F4F6',
    color: COLORS.textMuted,
    opacity: 0.7,
  },
  errorText: {
    color: COLORS.error,
    fontSize: 12,
    marginTop: 4,
  },
  row: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 8,
  },
  genderButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
    alignItems: 'center',
  },
  genderButtonActive: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  genderText: {
    color: COLORS.textDark,
    fontWeight: '600',
  },
  genderTextActive: {
    color: '#FFFFFF',
  },
  textDisabled: {
    color: COLORS.textMuted,
  },
  pillButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: COLORS.border,
    alignItems: 'center',
  },
  pillButtonActive: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  pillText: {
    color: COLORS.textDark,
    fontWeight: '600',
  },
  pillTextActive: {
    color: '#FFFFFF',
  },
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginVertical: 8,
  },
  checkBox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkBoxOn: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  checkMark: {
    color: '#fff',
    fontWeight: '900',
  },
  checkLabel: {
    color: COLORS.textDark,
    fontWeight: '600',
    flex: 1,
  },
  saveButton: {
    backgroundColor: COLORS.primary,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 20,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonSuccess: {
    backgroundColor: COLORS.success,
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 16,
  },
  infoBox: {
    marginTop: 12,
    padding: 12,
    backgroundColor: COLORS.primary + '10',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.primary,
  },
  infoText: {
    color: COLORS.textDark,
    fontSize: 13,
    textAlign: 'center',
  },
  checkLabelActive: {
    color: COLORS.primary,
    fontWeight: '600',
  },
});

export default DriverForm;